import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { supabase } from '@/supabase/client'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'
import { Copy, Gift, Users, CheckCircle, Clock, XCircle, Share2, PartyPopper, ArrowRight } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import { getActiveReferralEvent } from '@/supabase/referral/repository/referralEventRepository'
import { isEventOpen, refereeRewardLabel, type ReferralEventState } from '@/lib/referralEvent'

// 마일스톤 설정 (이벤트 보상 = normalReward × 이벤트 배수, admin-referral-approve와 동일)
const MILESTONES = [
  { count: 1, normalReward: 1 },
  { count: 2, normalReward: 1 },
  { count: 3, normalReward: 1 },
  { count: 4, normalReward: 1 },
  { count: 5, normalReward: 2 },
]

// 추천 상태 타입
interface Referral {
  id: number
  referee_id: string
  status: 'pending' | 'approved' | 'rejected'
  first_payment_at: string | null
  approved_at: string | null
  rejected_at: string | null
  rejection_reason: string | null
  is_event_period: boolean
  created_at: string
  referee_nickname?: string
}

// 내가 추천받은 정보 타입
interface MyReferralInfo {
  id: number
  referrer_nickname: string
  status: 'pending' | 'approved' | 'rejected'
  rejection_reason: string | null
  is_event_period: boolean
  event_id: number | null
  first_payment_at: string | null
}

export default function InvitePage() {
  const { user } = useAuth()
  const { openLoginModal } = useLoginModal()
  const navigate = useNavigate()
  
  const [myReferralCode, setMyReferralCode] = useState<string | null>(null)
  const [referrals, setReferrals] = useState<Referral[]>([])
  const [myReferralInfo, setMyReferralInfo] = useState<MyReferralInfo | null>(null)
  const [referralEvent, setReferralEvent] = useState<ReferralEventState>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [approvedCount, setApprovedCount] = useState(0)
  const [totalRewardMonths, setTotalRewardMonths] = useState(0)
  const [eventCountdown, setEventCountdown] = useState<string | null>(null)
  const [totalReferralCount, setTotalReferralCount] = useState(0)
  const [topReferrers, setTopReferrers] = useState<{ nickname: string; count: number }[]>([])


  // 데이터 로드
  useEffect(() => {
    if (!user) {
      setIsLoading(false)
      return
    }

    const loadData = async () => {
      setIsLoading(true)
      try {
        // 1. 내 초대 코드 가져오기
        const { data: codeData } = await supabase
          .from('referral_code')
          .select('code')
          .eq('user_id', user.id)
          .single()
        
        if (codeData) {
          setMyReferralCode(codeData.code)
        }

        // 2. 내가 추천한 사람들 가져오기
        const { data: referralsData } = await supabase
          .from('referral')
          .select(`
            id,
            referee_id,
            status,
            first_payment_at,
            approved_at,
            rejected_at,
            rejection_reason,
            is_event_period,
            created_at
          `)
          .eq('referrer_id', user.id)
          .order('created_at', { ascending: false })

        if (referralsData) {
          // 피추천인 닉네임 가져오기
          const refereeIds = referralsData.map(r => r.referee_id)
          const { data: membersData } = await supabase
            .from('member')
            .select('user_id, nickname')
            .in('user_id', refereeIds)

          const nicknameMap = new Map(membersData?.map(m => [m.user_id, m.nickname]) || [])
          
          const referralsWithNickname = referralsData.map(r => ({
            ...r,
            referee_nickname: nicknameMap.get(r.referee_id) || '알 수 없음'
          }))

          setReferrals(referralsWithNickname)

          // 승인된 추천 수 계산
          const approved = referralsWithNickname.filter(r => r.status === 'approved').length
          setApprovedCount(approved)

          // 총 보상 개월 계산
          const { data: rewardsData } = await supabase
            .from('referral_reward')
            .select('months_granted')
            .eq('user_id', user.id)
            .eq('recipient_type', 'referrer')

          const totalMonths = rewardsData?.reduce((sum, r) => sum + r.months_granted, 0) || 0
          setTotalRewardMonths(totalMonths)
        }

        // 3. 나를 추천한 사람 정보 가져오기 (내가 피추천인인 경우)
        const { data: myReferralData } = await supabase
          .from('referral')
          .select(`
            id,
            referrer_id,
            status,
            rejection_reason,
            is_event_period,
            event_id,
            first_payment_at
          `)
          .eq('referee_id', user.id)
          .single()

        if (myReferralData) {
          // 추천인 닉네임 가져오기
          const { data: referrerMember } = await supabase
            .from('member')
            .select('nickname')
            .eq('user_id', myReferralData.referrer_id)
            .single()

          setMyReferralInfo({
            id: myReferralData.id,
            referrer_nickname: referrerMember?.nickname || '알 수 없음',
            status: myReferralData.status,
            rejection_reason: myReferralData.rejection_reason,
            is_event_period: myReferralData.is_event_period,
            event_id: myReferralData.event_id,
            first_payment_at: myReferralData.first_payment_at,
          })
        }

        // 4. 현재 활성 이벤트 가져오기 (실패 시 undefined 유지 = 이벤트 표기 미노출)
        try {
          setReferralEvent(await getActiveReferralEvent())
        } catch (eventError) {
          console.error('[InvitePage] 이벤트 조회 오류:', eventError)
        }

        // 5. 총 초대장 가입자 수 (소셜 프루프용)
        const { count: totalCount } = await supabase
          .from('referral')
          .select('*', { count: 'exact', head: true })

        if (totalCount !== null) {
          setTotalReferralCount(totalCount)
        }

        // 6. 추천 랭킹 TOP 5 (가입 기준 - pending + approved)
        const { data: rankingData } = await supabase
          .from('referral')
          .select('referrer_id')
          .in('status', ['pending', 'approved'])

        if (rankingData && rankingData.length > 0) {
          // referrer_id별로 카운트
          const countMap = new Map<string, number>()
          rankingData.forEach(r => {
            countMap.set(r.referrer_id, (countMap.get(r.referrer_id) || 0) + 1)
          })

          // TOP 5 추출
          const top5Ids = Array.from(countMap.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)

          if (top5Ids.length > 0) {
            // 닉네임 조회
            const { data: membersData } = await supabase
              .from('member')
              .select('user_id, nickname')
              .in('user_id', top5Ids.map(([id]) => id))

            if (membersData) {
              const nicknameMap = new Map(membersData.map(m => [m.user_id, m.nickname || '익명']))
              const topList = top5Ids.map(([id, count]) => ({
                nickname: nicknameMap.get(id) || '익명',
                count
              }))
              setTopReferrers(topList)
            }
          }
        }

      } catch (error) {
        console.error('[InvitePage] 데이터 로드 오류:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [user])

  // 새 가입자에게 광고 가능한 이벤트(마감·종료 제외)
  const activeEvent = isEventOpen(referralEvent) ? referralEvent : null

  // 이벤트 카운트다운 타이머
  useEffect(() => {
    if (!activeEvent) {
      setEventCountdown(null)
      return
    }

    const calculateCountdown = () => {
      const now = new Date().getTime()
      const endTime = new Date(activeEvent.end_at).getTime()
      const diff = endTime - now

      if (diff <= 0) {
        setEventCountdown(null)
        return
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

      if (days >= 1) {
        setEventCountdown(`${days}일`)
      } else if (hours >= 1) {
        setEventCountdown(`${hours}시간`)
      } else {
        setEventCountdown(`${minutes}분`)
      }
    }

    calculateCountdown()
    const interval = setInterval(calculateCountdown, 60000) // 1분마다 업데이트

    return () => clearInterval(interval)
  }, [activeEvent])

  // 초대 링크 복사
  const copyInviteLink = () => {
    if (!myReferralCode) return
    const link = `${window.location.origin}/?code=${myReferralCode}`
    navigator.clipboard.writeText(link)
    toast.success('초대 링크가 복사되었습니다!')
  }


  // 닉네임 마스킹 (예: 홍길동 → 홍**)
  const maskNickname = (nickname: string) => {
    if (nickname.length <= 1) return nickname
    return nickname[0] + '*'.repeat(nickname.length - 1)
  }

  // 날짜 포맷
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`
  }

  // 이벤트 여부에 따른 보상 계산
  const isEventPeriod = !!activeEvent
  const eventMultiplier = activeEvent?.reward_multiplier ?? 1
  const eventCapText = activeEvent?.max_referees ? `선착순 ${activeEvent.max_referees}명` : null
  const getRewardForMilestone = (milestone: typeof MILESTONES[0]) => {
    return milestone.normalReward * eventMultiplier
  }
  const refereeLabel = myReferralInfo ? refereeRewardLabel(myReferralInfo, referralEvent) : null

  // 비로그인 상태
  if (!user) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Helmet>
          <title>초대장 - 셀러헬퍼</title>
        </Helmet>
        <Card className="text-center py-12">
          <CardContent>
            <Gift className="w-16 h-16 mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-bold mb-2">로그인이 필요합니다</h2>
            <p className="text-gray-500 mb-4">초대장 기능을 이용하려면 로그인해주세요.</p>
            <Button onClick={() => openLoginModal()}>로그인</Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <Helmet>
        <title>초대장 - 셀러헬퍼</title>
      </Helmet>

      {/* 피추천인 섹션 (첫 결제 전에만 표시 - 90% 할인 혜택 안내) */}
      {myReferralInfo && myReferralInfo.status === 'pending' && (
        <Card className="mb-6 overflow-hidden border-purple-500">
          {/* 상단 배너 */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 text-white px-4 py-3 text-center">
            <p className="text-sm font-bold flex items-center justify-center gap-2">
              <Gift className="w-4 h-4" />
              친구 초대 특별 혜택 적용 중!
            </p>
          </div>
          
          <CardContent className="pt-5 bg-gradient-to-b from-purple-50 to-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-purple-100">
                <Gift className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-gray-800">
                  <span className="font-bold text-purple-700">{maskNickname(myReferralInfo.referrer_nickname)}</span>님의 초대로 가입하셨습니다
                </p>
                
                <div className="mt-4 p-4 bg-white rounded-xl border-2 border-purple-200 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🎁</span>
                    <p className="text-purple-700 font-bold">첫 달 특별 혜택</p>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                      90% 할인
                    </p>
                    <p className="text-sm text-gray-500 line-through">정가 결제</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    첫 결제 시 자동 적용
                  </p>
                  
                  {refereeLabel && refereeLabel.kind !== 'hidden' && (
                    <div className="mt-4 pt-3 border-t border-purple-100">
                      <p className="text-xs text-purple-600 font-medium flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {refereeLabel.kind === 'event'
                          ? `첫 결제 후 1시간 내 승인 → 추가 +${refereeLabel.months}개월 보상!${refereeLabel.maxReferees ? ` (선착순 ${refereeLabel.maxReferees}명)` : ''}`
                          : refereeLabel.kind === 'confirm'
                            ? '첫 결제 후 승인 시 추가 혜택이 확정됩니다.'
                            : '첫 결제 후 1시간 내 승인 → 추가 +1개월 보상!'}
                      </p>
                    </div>
                  )}
                  
                  <Button
                    onClick={() => navigate('/mypage')}
                    className="w-full mt-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold"
                  >
                    지금 구독하고 90% 할인 받기
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* 이벤트 배너 */}
      {activeEvent && (
        <Card className="mb-6 border-purple-500 bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 overflow-hidden">
          <CardContent className="py-5">
            <div className="flex items-center justify-between gap-4">
              {/* 왼쪽: 이벤트 정보 */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <PartyPopper className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-bold text-white text-lg">🎉 {activeEvent.name}</p>
                  <p className="text-sm text-white/90">
                    보상 {eventMultiplier}배!{eventCapText && <> <span className="text-yellow-300 font-bold">{eventCapText}</span></>}
                  </p>
                </div>
              </div>
              
              {/* 가운데 + 오른쪽: 카운트다운 */}
              {eventCountdown && (
                <div className="flex items-center justify-end gap-2 flex-1">
                  <span className="text-white/80 text-lg font-medium">종료까지</span>
                  <span className="text-3xl sm:text-4xl font-black text-yellow-300">{eventCountdown}</span>
                  <span className="text-white/80 text-lg font-medium">남음!</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 소셜 프루프 & 랭킹 */}
      {(totalReferralCount > 0 || topReferrers.length > 0) && (
        <Card className="mb-6 bg-white border border-gray-200 shadow-sm">
          <CardContent className="pt-5 pb-5">
            {/* 총 가입자 수 */}
            {totalReferralCount > 0 && (
              <p className="text-center text-gray-700 font-medium flex items-center justify-center gap-2 mb-4">
                <Users className="w-4 h-4 text-gray-500" />
                총 <span className="font-bold text-brand-primary text-lg">{totalReferralCount.toLocaleString()}명</span>이 초대장으로 가입했어요!
              </p>
            )}
            
            {/* 추천 랭킹 TOP 5 */}
            {topReferrers.length > 0 && (
              <div className={totalReferralCount > 0 ? "border-t border-gray-100 pt-4" : ""}>
                <p className="text-center text-sm text-gray-500 font-medium mb-3">🏆 추천왕 랭킹 TOP 5</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {topReferrers.map((referrer, index) => (
                    <div
                      key={index}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                        index === 0
                          ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                          : index === 1
                            ? 'bg-gray-50 text-gray-600 border border-gray-200'
                            : index === 2
                              ? 'bg-orange-50 text-orange-700 border border-orange-200'
                              : 'bg-gray-50 text-gray-500 border border-gray-100'
                      }`}
                    >
                      <span className="font-bold">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `${index + 1}.`}
                      </span>
                      <span className="font-medium">{maskNickname(referrer.nickname)}</span>
                      <span className="text-xs opacity-70">({referrer.count}명)</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* 내 초대 코드 & 공유 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share2 className="w-5 h-5" />
            내 초대 코드
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-100 rounded-lg p-4 text-center mb-4">
            <p className="text-3xl font-bold tracking-widest text-brand-primary">
              {myReferralCode || '코드 없음'}
            </p>
          </div>
          <div className="flex justify-center">
            <Button variant="outline" onClick={copyInviteLink} className="flex items-center gap-2">
              <Copy className="w-4 h-4" />
              링크 복사
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* 마일스톤 프로그레스 */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Gift className="w-5 h-5" />
            추천 보상
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* 현재 상태 */}
          <div className={`rounded-lg p-4 mb-6 text-center ${isEventPeriod ? 'bg-purple-100 border-2 border-purple-300' : 'bg-brand-primary/10'}`}>
            {isEventPeriod && (
              <div className="mb-2 inline-flex items-center gap-1 px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                <PartyPopper className="w-3 h-3" />
                보상 {eventMultiplier}배 이벤트{eventCapText && ` (${eventCapText})`}
              </div>
            )}
            <p className="text-sm text-gray-600 mb-1">현재까지 승인된 추천</p>
            <p className={`text-3xl font-bold ${isEventPeriod ? 'text-purple-600' : 'text-brand-primary'}`}>{approvedCount}명</p>
            <p className="text-sm text-gray-500 mt-1">누적 보상: +{totalRewardMonths}개월</p>
          </div>

          {/* 마일스톤 바 */}
          <div className="relative mb-8">
            {/* 프로그레스 라인 */}
            <div className="absolute top-4 left-0 right-0 h-1 bg-gray-200 rounded-full">
              <div 
                className="h-full bg-brand-primary rounded-full transition-all duration-500"
                style={{ width: `${Math.min((approvedCount / 5) * 100, 100)}%` }}
              />
            </div>

            {/* 마일스톤 포인트 */}
            <div className="relative flex justify-between">
              {MILESTONES.map((milestone) => {
                const isCompleted = approvedCount >= milestone.count
                const isCurrent = approvedCount === milestone.count - 1
                const reward = getRewardForMilestone(milestone)
                
                return (
                  <div key={milestone.count} className="flex flex-col items-center" style={{ width: '18%' }}>
                    {/* 포인트 */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold z-10 transition-all ${
                      isCompleted 
                        ? 'bg-brand-primary text-white' 
                        : isCurrent
                          ? 'bg-white border-2 border-brand-primary text-brand-primary'
                          : 'bg-gray-200 text-gray-500'
                    }`}>
                      {isCompleted ? '✓' : milestone.count}
                    </div>
                    
                    {/* 보상 정보 */}
                    <div className={`mt-3 text-center ${isCompleted ? 'text-brand-primary' : 'text-gray-500'}`}>
                      <p className="text-xs font-medium">{milestone.count}명</p>
                      {isEventPeriod ? (
                        <div className="relative">
                          <p className="text-[10px] text-gray-400 line-through">
                            +{milestone.normalReward}개월
                          </p>
                          <p className={`text-sm font-bold text-purple-600`}>
                            +{reward}개월 🎉
                          </p>
                        </div>
                      ) : (
                        <p className={`text-sm font-bold ${isCompleted ? 'text-brand-primary' : ''}`}>
                          +{reward}개월
                        </p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* 안내 메시지 */}
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
            <p className="mb-2">📌 <strong>추천 보상 안내</strong></p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>피추천인이 첫 결제를 완료하고 관리자 승인 후 보상이 지급됩니다.</li>
              <li>추천인은 승인 시점에 구독 중이어야 보상을 받을 수 있습니다.</li>
              <li>본인 중복 가입 등 부정 참여가 확인되면 보상이 취소될 수 있습니다.</li>
              {isEventPeriod && <li className="text-purple-600 font-medium">🎉 이벤트 중 보상 {eventMultiplier}배!{eventCapText && ` (결제 순서 기준 ${eventCapText})`}</li>}
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* 내가 초대한 사람들 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            내가 초대한 사람들
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>아직 초대한 사람이 없습니다.</p>
              <p className="text-sm mt-1">위의 초대 링크를 공유해보세요!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {referrals.map((referral, index) => (
                <div 
                  key={referral.id}
                  className={`p-3 rounded-lg border ${
                    referral.status === 'approved' ? 'bg-green-50 border-green-200' :
                    referral.status === 'rejected' ? 'bg-red-50 border-red-200' :
                    'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-500">{index + 1}.</span>
                      <div>
                        <p className="font-medium">
                          {maskNickname(referral.referee_nickname || '알 수 없음')}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(referral.created_at)}
                          {referral.is_event_period && <span className="ml-1 text-purple-500">🎉 이벤트</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      {/* 결제 상태 */}
                      {referral.first_payment_at ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="hidden sm:inline">결제완료</span>
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-gray-400">
                          <Clock className="w-4 h-4" />
                          <span className="hidden sm:inline">결제대기</span>
                        </span>
                      )}
                      
                      {/* 승인 상태 */}
                      {referral.status === 'approved' && (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />
                          <span className="hidden sm:inline">승인완료</span>
                        </span>
                      )}
                      {referral.status === 'rejected' && (
                        <span className="flex items-center gap-1 text-red-500">
                          <XCircle className="w-4 h-4" />
                          <span className="hidden sm:inline">거절됨</span>
                        </span>
                      )}
                      {referral.status === 'pending' && referral.first_payment_at && (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <Clock className="w-4 h-4" />
                          <span className="hidden sm:inline">승인대기</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {/* 거절 사유 표시 */}
                  {referral.status === 'rejected' && referral.rejection_reason && (
                    <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-700">
                      <span className="font-medium">거절 사유:</span> {referral.rejection_reason}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
